import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, KeyboardAvoidingView, Platform } from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { useLocalSearchParams, useRouter } from "expo-router";
import {
  doc, onSnapshot, collection, query, orderBy, addDoc, Timestamp, updateDoc, increment,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useLiked } from "@/hooks/useLikes";
import { toggleLike } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { Post, Comment } from "@/lib/types";

function timeAgo(ts: any): string {
  if (!ts?.toDate) return "";
  const diff = Date.now() - ts.toDate().getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ── Styled Components ──

const Screen = styled.View`
  flex: 1;
  background-color: ${colors.bg};
`;

const PostCard = styled.View`
  background-color: ${colors.white};
  padding: 20px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const AuthorRow = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: 12px;
`;

const Avatar = styled.View`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const AvatarText = styled.Text`
  font-size: 16px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const AuthorInfo = styled.View`
  flex: 1;
`;

const AuthorName = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const PostTime = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
`;

const PostText = styled.Text`
  font-size: 16px;
  color: ${colors.ink};
  line-height: 24px;
`;

const ActionsRow = styled.View`
  flex-direction: row;
  gap: 24px;
  margin-top: 16px;
  padding-top: 12px;
  border-top-width: 1px;
  border-top-color: ${colors.line};
`;

const ActionBtn = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const ActionCount = styled.Text<{ active?: boolean }>`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${(p) => p.active ? colors.danger : colors.inkMuted};
`;

const CommentsSection = styled.View`
  padding: 16px 20px 0;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const CommentCard = styled.View`
  flex-direction: row;
  margin-bottom: 16px;
`;

const CommentAvatar = styled.View`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${colors.brand50};
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  margin-top: 2px;
`;

const CommentAvatarText = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const CommentBody = styled.View`
  flex: 1;
  background-color: ${colors.white};
  border-radius: ${radius.md}px;
  padding: 10px 14px;
  border: 1px solid ${colors.line};
`;

const CommentAuthor = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const CommentText = styled.Text`
  font-size: 14px;
  color: ${colors.ink};
  line-height: 20px;
  margin-top: 2px;
`;

const CommentTime = styled.Text`
  font-size: 11px;
  color: ${colors.inkMuted};
  margin-top: 4px;
`;

const InputBar = styled.View`
  flex-direction: row;
  align-items: flex-end;
  padding: 10px 20px;
  background-color: ${colors.white};
  border-top-width: 1px;
  border-top-color: ${colors.line};
`;

const CommentInput = styled.TextInput`
  flex: 1;
  min-height: 40px;
  max-height: 100px;
  background-color: ${colors.bg};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 10px 14px;
  font-size: 14px;
  color: ${colors.ink};
  margin-right: 10px;
`;

const SendBtn = styled.View<{ disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${(p) => p.disabled ? colors.line : colors.brand};
  align-items: center;
  justify-content: center;
`;

const SendBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
  padding: 20px;
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

// ── Component ──

export default function PostDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { club, membership } = useClub();
  const [post, setPost] = useState<(Post & { id: string }) | null>(null);
  const [comments, setComments] = useState<(Comment & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [sending, setSending] = useState(false);

  const liked = useLiked(club?.id, id, user?.uid);

  // Listen to post
  useEffect(() => {
    if (!club?.id || !id) return;
    const unsub = onSnapshot(doc(db, "clubs", club.id, "posts", id), (snap) => {
      if (snap.exists()) {
        setPost({ id: snap.id, ...snap.data() } as Post & { id: string });
      }
      setLoading(false);
    });
    return unsub;
  }, [club?.id, id]);

  // Listen to comments
  useEffect(() => {
    if (!club?.id || !id) return;
    const q = query(
      collection(db, "clubs", club.id, "posts", id, "comments"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as (Comment & { id: string })[]);
    });
    return unsub;
  }, [club?.id, id]);

  const handleLike = async () => {
    if (!club || !user || !id || !membership) return;
    await toggleLike(club.id, id, user.uid, membership.displayName, liked);
  };

  const handleComment = async () => {
    if (!club || !user || !membership || !id || !commentText.trim()) return;
    setSending(true);
    await addDoc(collection(db, "clubs", club.id, "posts", id, "comments"), {
      authorId: user.uid,
      authorName: membership.displayName,
      text: commentText.trim(),
      createdAt: Timestamp.now(),
    });
    await updateDoc(doc(db, "clubs", club.id, "posts", id), {
      commentCount: increment(1),
    });
    setCommentText("");
    setSending(false);
  };

  if (loading) {
    return <LoadingContainer><ActivityIndicator size="large" color={colors.brand} /></LoadingContainer>;
  }

  if (!post) {
    return <LoadingContainer><EmptyText>Post not found.</EmptyText></LoadingContainer>;
  }

  return (
    <Screen>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === "ios" ? "padding" : "height"} keyboardVerticalOffset={90}>
        <KeyboardAwareScroll style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
          <ContentWidth>
            {/* Post */}
            <PostCard>
              <AuthorRow>
                <Avatar>
                  <AvatarText>{post.authorName?.[0]?.toUpperCase()}</AvatarText>
                </Avatar>
                <AuthorInfo>
                  <AuthorName>{post.authorName}</AuthorName>
                  <PostTime>{timeAgo(post.createdAt)}</PostTime>
                </AuthorInfo>
              </AuthorRow>
              <PostText>{post.text}</PostText>
              <ActionsRow>
                <Pressable onPress={handleLike}>
                  <ActionBtn>
                    <ActionCount active={liked}>{liked ? "❤️" : "🤍"} {post.likeCount}</ActionCount>
                  </ActionBtn>
                </Pressable>
                <ActionBtn>
                  <ActionCount>💬 {post.commentCount}</ActionCount>
                </ActionBtn>
              </ActionsRow>
            </PostCard>

            {/* Comments */}
            <CommentsSection>
              <SectionLabel>Comments ({comments.length})</SectionLabel>
              {comments.map((c) => (
                <CommentCard key={c.id}>
                  <CommentAvatar>
                    <CommentAvatarText>{c.authorName?.[0]?.toUpperCase()}</CommentAvatarText>
                  </CommentAvatar>
                  <CommentBody>
                    <CommentAuthor>{c.authorName}</CommentAuthor>
                    <CommentText>{c.text}</CommentText>
                    <CommentTime>{timeAgo(c.createdAt)}</CommentTime>
                  </CommentBody>
                </CommentCard>
              ))}
              {comments.length === 0 && (
                <EmptyText>No comments yet. Be the first!</EmptyText>
              )}
            </CommentsSection>
          </ContentWidth>
        </KeyboardAwareScroll>

        {/* Comment input */}
        <ContentWidth>
          <InputBar style={{ paddingBottom: Math.max(insets.bottom, 10) }}>
            <CommentInput
              placeholder="Write a comment..."
              placeholderTextColor={colors.inkMuted}
              value={commentText}
              onChangeText={setCommentText}
              multiline
            />
            <Pressable onPress={handleComment} disabled={!commentText.trim() || sending}>
              <SendBtn disabled={!commentText.trim() || sending}>
                <SendBtnText>↑</SendBtnText>
              </SendBtn>
            </Pressable>
          </InputBar>
        </ContentWidth>
      </KeyboardAvoidingView>
    </Screen>
  );
}
